import { useEffect, useRef, useState } from "react";
import { OpenSheetMusicDisplay } from "opensheetmusicdisplay";
import QuestionLayout from "./QuestionLayout";

/* ───────────────────────────────────────────── */

export default function DrawBarlinesWidget({
  config,
  value = {},
  onChange,
  mode,
  feedback
}) {
  const containerRef = useRef(null);
  const osmdRef = useRef(null);
  const layerRef = useRef(null);
  const initializedRef = useRef(false);

  const [activeSlot, setActiveSlot] = useState(null);
  const [activeType, setActiveType] = useState("single");

  const modeRef = useRef(mode);
  const valueRef = useRef(value);
  const activeTypeRef = useRef(activeType);

  /* ───────── MODE REF ───────── */

  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    activeTypeRef.current = activeType;
  }, [activeType]);

  /* ───────── OSMD INIT (ONCE, NO RESIZE EVER) ───────── */

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    const osmd = new OpenSheetMusicDisplay(containerRef.current, {
      drawTitle: false,
      drawComposer: false,
      drawPartNames: false,
      drawMeasureNumbers: false,
      autoResize: false // 🔒 HARD LOCK
    });

    osmdRef.current = osmd;

    (async () => {
      await osmd.load(config.musicXML);
      // Keep the entire excerpt on one system, as in MissingRhythmWidget.
      // QuestionLayout owns horizontal scrolling around the fixed surface.
      osmd.EngravingRules.RenderXMeasuresPerLineAkaSystem =
        osmd.Sheet?.SourceMeasures?.length || 1;
      osmd.render();

      // wait for layout + DOM paint
      await new Promise(r =>
        requestAnimationFrame(() =>
          requestAnimationFrame(r)
        )
      );

      hideInitialBarlines();
      ensureLayer();
      redraw();
    })();
  }, []);

  /* ───────── REDRAW TRIGGERS (STATE ONLY) ───────── */

  useEffect(() => {
    if (!initializedRef.current) return;
    redraw();
  }, [activeSlot, activeType, value, mode]);

  /* ───────── SVG LAYER ───────── */

  function ensureLayer() {
    const svg = containerRef.current?.querySelector("svg");
    if (!svg) return;

    const rootG = svg.querySelector("g");
    if (!rootG) return;

    let layer = rootG.querySelector("#barline-layer");
    if (!layer) {
      layer = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "g"
      );
      layer.setAttribute("id", "barline-layer");
      rootG.appendChild(layer);
    }

    layerRef.current = layer;
  }

  function clearLayer() {
    if (layerRef.current) {
      layerRef.current.innerHTML = "";
    }
  }

  /* ───────── STAFF GEOMETRY ───────── */

  function getStaffExtents(svg, svgRect) {
    const initialLines = Array.from(svg.querySelectorAll("path"))
      .filter(path => path.getAttribute("d")?.startsWith("M50"))
      .map(path => path.getBoundingClientRect())
      .slice(0, 5);

    if (initialLines.length < 5) return null;

    const top = Math.min(...initialLines.map(rect => rect.top));
    const bottom = Math.max(...initialLines.map(rect => rect.bottom));
    // Staff lines can be split across measures. Find their actual right edge
    // instead of guessing the ending position from the final note's spacing.
    const staffLines = Array.from(svg.querySelectorAll("path, line"))
      .map(element => element.getBoundingClientRect())
      .filter(rect =>
        rect.width > 40 && rect.height < 3 &&
        rect.top >= top - 2 && rect.bottom <= bottom + 2
      );
    const right = Math.max(...initialLines.map(rect => rect.right),
      ...staffLines.map(rect => rect.right));

    return {
      topY: top - svgRect.top,
      bottomY: bottom - svgRect.top,
      rightX: right - svgRect.left
    };
  }

  /* ───────── NOTE CENTERS ───────── */

  function collectRhythmicCenters(svg) {
    // Use the rendered note positions at every level. Saved viewport
    // coordinates become incorrect when the score width or scroll changes.
    {
      const renderedCenters = [];
      const osmd = osmdRef.current;
      let systemCount = 0;

      osmd?.GraphicSheet?.MusicPages?.forEach(page => {
        page.musicSystems?.forEach(system => {
          systemCount += 1;

          system.graphicalMeasures?.forEach(measureGroup => {
            const measure = measureGroup?.[0];
            if (!measure) return;

            measure.staffEntries?.forEach(staffEntry => {
              let x = null;

              outer:
              for (const gve of staffEntry.graphicalVoiceEntries || []) {
                for (const note of gve.notes || []) {
                  const el = note.vfnote?.[0]?.attrs?.el;
                  if (!el) continue;

                  const r = el.getBoundingClientRect();
                  if (r.width || r.height) {
                    x = r.left + r.width / 2;
                    break outer;
                  }
                }
              }

              if (x !== null) renderedCenters.push({ x });
            });
          });
        });
      });

      if (systemCount === 1 && renderedCenters.length > 1) {
        renderedCenters.sort((a, b) => a.x - b.x);

        const clusters = [];
        for (const item of renderedCenters) {
          const last = clusters[clusters.length - 1];
          if (!last || Math.abs(last.x - item.x) > 8) {
            clusters.push({ x: item.x, count: 1 });
          } else {
            last.x =
              (last.x * last.count + item.x) /
              (last.count + 1);
            last.count += 1;
          }
        }

        return clusters.map(({ x }) => ({ x }));
      }
    }

    //console.log(config);

    switch(config.level) {
      case 1:
        return [{"x":137.18239974975586},{"x":172.73919677734375},{"x":197.48751831054688},{"x":237.6224822998047},{"x":277.75743103027344},{"x":354.1155090332031},{"x":385.4715270996094},{"x":418.6246337890625},{"x":482.56060791015625},{"x":530.8486022949219},{"x":562.2046203613281},{"x":609.2085876464844},{"x":654.2085876464844}];
      case 2:
        return [{"x":137.18239974975586},{"x":172.72480010986328},{"x":197.48751831054688},{"x":244.85946655273438},{"x":290.4342956542969},{"x":357.1400604248047},{"x":438.4172058105469},{"x":492.41111755371094},{"x":543.7006225585938},{"x":579.7955932617188}];
      case 3:
        return [{"x":137.18239974975586},{"x":172.72480010986328},{"x":202.99208068847656},{"x":311.76922607421875},{"x":350.10255432128906},{"x":388.4358825683594},{"x":442.6943359375},{"x":494.6206359863281},{"x":543.7006225585938},{"x":579.7955932617188}];
      case 4:
        return [{"x":137.18239974975586},{"x":172.72480010986328},{"x":197.48751831054688},{"x":250.3158950805664},{"x":279.387939453125},{"x":321.1625061035156},{"x":350.2345504760742},{"x":412.7006072998047},{"x":492.1806335449219},{"x":528.5816040039062},{"x":566.7796936035156},{"x":620.6257019042969},{"x":656.6196594238281}];
      case 5:
        return [{"x":137.18239974975586},{"x":172.83999633789062},{"x":195.89199829101562},{"x":232.90784454345703},{"x":262.5283393859863},{"x":281.24070739746094},{"x":307.7572937011719},{"x":346.5702362060547},{"x":406.17791748046875},{"x":429.99417877197266},{"x":476.8888244628906},{"x":506.22105407714844},{"x":536.2412719726562},{"x":565.2908477783203},{"x":598.0950012207031},{"x":629.2243957519531},{"x":658.5566101074219},{"x":705.5216369628906},{"x":734.9601135253906},{"x":762.6014099121094},{"x":795.7473297119141},{"x":864.1258850097656},{"x":885.9082641601562},{"x":904.7496032714844},{"x":929.4729919433594},{"x":951.2553405761719},{"x":999.6532592773438},{"x":1048.0511474609375}];  
      case 6:
          return [{"x":137.18239974975586},{"x":172.83999633789062},{"x":197.68911743164062},{"x":234.56970977783203},{"x":263.3447952270508},{"x":281.5740661621094},{"x":307.4867858886719},{"x":344.3673553466797},{"x":400.11029052734375},{"x":423.20191192626953},{"x":468.5419158935547},{"x":495.1114807128906},{"x":522.3689575195312},{"x":550.0372772216797},{"x":579.6629943847656},{"x":608.0296936035156},{"x":666.0039978027344},{"x":694.2757873535156},{"x":720.7504577636719},{"x":752.7296905517578},{"x":817.0915832519531},{"x":838.1885681152344},{"x":856.5158996582031},{"x":880.3825988769531},{"x":901.4796142578125},{"x":949.6325836181641},{"x":1036.362548828125}];
      case '1p':
        return [{"x":137.93120193481445},{"x":172.73919677734375},{"x":197.48751831054688},{"x":256.2483215332031},{"x":330.90582275390625},{"x":364.15582275390625},{"x":397.40582275390625},{"x":430.65582275390625},{"x":478.91835021972656},{"x":510.2743377685547},{"x":541.6303405761719},{"x":608.2479553222656}];
        break;
      case '2p':
        return [{"x":137.18239974975586},{"x":172.73919677734375},{"x":197.48751831054688},{"x":256.2483215332031},{"x":330.90582275390625},{"x":364.15582275390625},{"x":397.40582275390625},{"x":430.65582275390625},{"x":478.91835021972656},{"x":516.1820297241211},{"x":594.0234069824219}];
        break;
      case '3p':
        return [{"x":137.18239974975586},{"x":172.73919677734375},{"x":195.69039916992188},{"x":237.93591690063477},{"x":333.1337890625},{"x":379.6246643066406},{"x":410.9806671142578},{"x":459.78175354003906},{"x":493.03175354003906},{"x":526.2817687988281},{"x":559.5317687988281},{"x":610.4759216308594}];
        break;
      case '4pSp26':
        return [{"x":137.18239974975586},{"x":172.72480010986328},{"x":197.75428009033203},{"x":270.0070037841797},{"x":330.90582275390625},{"x":355.48921966552734},{"x":387.98561096191406},{"x":427.0471496582031},{"x":483.19549560546875},{"x":523.8136291503906},{"x":557.2636260986328},{"x":594.4682312011719},{"x":656.9287872314453}];
        break;
      case '4p':
        return [{"x":137.18239974975586},{"x":172.73919677734375},{"x":197.75428009033203},{"x":256.5150833129883},{"x":336.41038513183594},{"x":402.1330261230469},{"x":424.05238342285156},{"x":471.65257263183594},{"x":554.9949340820312},{"x":591.8889465332031},{"x":614.985595703125},{"x":645.0040588378906},{"x":665.1009216308594}];
        break;
      case '5p':
        return [{"x":137.18239974975586},{"x":172.72480010986328},{"x":197.75428009033203},{"x":270.0070037841797},{"x":330.90582275390625},{"x":358.4081039428711},{"x":397.56640625},{"x":446.20875549316406},{"x":471.8830261230469},{"x":493.64327239990234},{"x":523.2315368652344},{"x":562.6006469726562},{"x":601.6622009277344},{"x":626.2455749511719},{"x":660.5390930175781},{"x":722.1919860839844}];
        break;
      case '5pSp26':
        return [{"x":137.93120193481445},{"x":172.73919677734375},{"x":195.69039916992188},{"x":249.09056091308594},{"x":277.1684875488281},{"x":310.9329528808594},{"x":342.40173721313477},{"x":362.71568298339844},{"x":388.60704040527344},{"x":414.49839782714844},{"x":479.8440246582031},{"x":516.7380218505859},{"x":551.8348999023438},{"x":591.041259765625},{"x":614.3879089355469},{"x":660.4570617675781},{"x":744.066162109375},{"x":789.0661926269531}];
        break;
      case '6p':
        return [{"x":137.18239974975586},{"x":172.72480010986328},{"x":197.48751831054688},{"x":225.45525360107422},{"x":248.93561935424805},{"x":281.3907165527344},{"x":309.3584442138672},{"x":367.17466735839844},{"x":398.1171417236328},{"x":457.37030029296875},{"x":494.64453125},{"x":535.6733093261719},{"x":585.0490417480469},{"x":610.9066467285156},{"x":632.8044128417969},{"x":662.6218566894531},{"x":701.836181640625},{"x":740.8977355957031},{"x":765.4811248779297},{"x":799.7746276855469},{"x":856.1897583007812},{"x":910.0947570800781}];
        break;
    }
    // Level 4 return [{"x":137.18239974975586},{"x":172.72480010986328},{"x":197.48751831054688},{"x":244.99472045898438},{"x":292.5019226074219},{"x":355.9126281738281},{"x":407.83892822265625},{"x":456.9188995361328},{"x":500.9734344482422},{"x":526.7552795410156},{"x":561.9370727539062},{"x":596.5507049560547},{"x":650.7893371582031},{"x":686.7954406738281}];
    // Level 5 return [{"x":137.93120193481445},{"x":172.83999633789062},{"x":203.1936798095703},{"x":285.84619140625},{"x":309.6011047363281},{"x":340.59932708740234},{"x":357.85633850097656},{"x":383.6202392578125},{"x":409.8235778808594},{"x":459.7467956542969},{"x":490.97804260253906},{"x":517.343147277832},{"x":538.2721252441406},{"x":560.0429077148438},{"x":593.0712585449219},{"x":639.3126525878906},{"x":691.9860992431641},{"x":745.9633178710938},{"x":781.3072814941406},{"x":817.3520965576172},{"x":857.3283996582031},{"x":887.3960266113281},{"x":915.6665344238281},{"x":949.2149658203125},{"x":970.6756286621094},{"x":995.6171264648438}]
    // Level 6 return [{"x":137.93120193481445},{"x":172.83999633789062},{"x":203.1936798095703},{"x":285.84619140625},{"x":309.6011047363281},{"x":340.59932708740234},{"x":357.85633850097656},{"x":383.6202392578125},{"x":409.8235778808594},{"x":459.7467956542969},{"x":490.97804260253906},{"x":517.343147277832},{"x":538.2721252441406},{"x":560.0429077148438},{"x":593.0712585449219},{"x":639.3126525878906},{"x":691.9860992431641},{"x":745.9633178710938},{"x":781.3072814941406},{"x":817.3520965576172},{"x":857.3283996582031},{"x":887.3960266113281},{"x":915.6665344238281},{"x":949.2149658203125},{"x":970.6756286621094},{"x":995.6171264648438}]

    //OLD EXAMS
    // case 4:
    //     return [{"x":137.18239974975586},{"x":172.72480010986328},{"x":197.48751831054688},{"x":244.99472045898438},{"x":292.5019226074219},{"x":355.9126281738281},{"x":407.83892822265625},{"x":456.9188995361328},{"x":500.9734344482422},{"x":526.7552795410156},{"x":561.9370727539062},{"x":596.5507049560547},{"x":650.7893371582031},{"x":686.7954406738281}];
    //     break;
    //   case 5:
    //     return [{"x":137.93120193481445},{"x":172.83999633789062},{"x":203.1936798095703},{"x":285.84619140625},{"x":309.6011047363281},{"x":340.59932708740234},{"x":357.85633850097656},{"x":383.6202392578125},{"x":409.8235778808594},{"x":459.7467956542969},{"x":490.97804260253906},{"x":517.343147277832},{"x":538.2721252441406},{"x":560.0429077148438},{"x":593.0712585449219},{"x":639.3126525878906},{"x":691.9860992431641},{"x":745.9633178710938},{"x":781.3072814941406},{"x":817.3520965576172},{"x":857.3283996582031},{"x":887.3960266113281},{"x":915.6665344238281},{"x":949.2149658203125},{"x":970.6756286621094},{"x":995.6171264648438}]
    //     break;
    //   case 6:
    //    return [{"x":137.18239974975586},{"x":173.0271987915039},{"x":198.0635223388672},{"x":236.0514144897461},{"x":258.31016540527344},{"x":292.0575408935547},{"x":330.0454406738281},{"x":370.5378875732422},{"x":406.02122497558594},{"x":464.5600128173828},{"x":524.5189819335938},{"x":555.6128540039062},{"x":580.7235107421875},{"x":602.0610504150391},{"x":630.9447937011719},{"x":671.4059143066406},{"x":718.7407379150391},{"x":756.4059143066406},{"x":803.7407379150391},{"x":862.0807037353516},{"x":911.1821441650391}]
    //    break;

    const items = [];

    Array.from(svg.querySelectorAll("path, ellipse")).forEach(el => {
      const r = el.getBoundingClientRect();
      if (r.width < 4 || r.height < 4) return;
      if (r.width > 100 && r.height < 3) return;
      if (r.height == 41) return;
      items.push({ x: r.left + r.width / 2 });
    });

    items.sort((a, b) => a.x - b.x);

    const clusters = [];
    const CLUSTER_X = 14;

    for (const c of items) {
      const last = clusters[clusters.length - 1];
      if (!last || Math.abs(last.x - c.x) > CLUSTER_X) {
        clusters.push({ x: c.x });
      } else {
        last.x = (last.x + c.x) / 2;
      }
    }

    console.log(JSON.stringify(clusters));
    return clusters;
  }

  /* ───────── DRAW BARLINE ───────── */

  function drawBarline(x, topY, bottomY, type, ghost = false) {
    const layer = layerRef.current;

    const draw = (dx, fat = false) => {
      const l = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "line"
      );
      l.setAttribute("x1", x + dx);
      l.setAttribute("x2", x + dx);
      l.setAttribute("y1", topY);
      l.setAttribute("y2", bottomY);
      l.setAttribute("stroke", ghost ? "#999" : "#000");
      l.setAttribute("stroke-width", fat ? 2.6 : 1.4);
      if (ghost) l.setAttribute("stroke-dasharray", "4 3");
      layer.appendChild(l);
    };

    type === "single"
      ? draw(0)
      : (draw(-2), draw(3, true));
  }

  /* ───────── HIDE ORIGINAL BARLINES (ONCE) ───────── */

  function hideInitialBarlines() {
    const svg = containerRef.current?.querySelector("svg");
    if (!svg) return;

    const rects = Array.from(svg.querySelectorAll("rect"))
      .map(r => ({ r, x: r.getBoundingClientRect().left }))
      .sort((a, b) => a.x - b.x)
      .map(o => o.r);

    rects.forEach((r, index) => {
      if (!config.testEndBarline) {
        if (index >= rects.length - 2) return;
      }

      const bb = r.getBoundingClientRect();
      if (bb.height > 40 && bb.width < 4) {
        r.style.visibility = "hidden";
      }
    });
  }

  /* ───────── REDRAW ───────── */

  function redraw() {
    ensureLayer();

    const layer = layerRef.current;
    const svg = containerRef.current?.querySelector("svg");
    if (!layer || !svg) return;

    clearLayer();

    const svgRect = svg.getBoundingClientRect();
    const staff = getStaffExtents(svg, svgRect);
    if (!staff) return;

    const notes = collectRhythmicCenters(svg);

    const slots = notes.slice(1).map((note, index) => ({
      x: (notes[index].x + note.x) / 2 - svgRect.left,
      slotId: `gap_${index}`
    }));

    if (config.testEndBarline && notes.length > 0) {
      slots.push({
        // The thick stroke extends 4.3px right of the double-bar anchor.
        // Keep that stroke inside the staff, even for a short final note.
        x: staff.rightX - 4.5,
        slotId: "end"
      });
    }

    for (const { x, slotId } of slots) {

      /* placed barline */
      if (value[slotId]) {
        drawBarline(x, staff.topY, staff.bottomY, value[slotId]);
      }

      /* hover preview */
      if (activeSlot === slotId && modeRef.current !== "review") {
        drawBarline(x, staff.topY, staff.bottomY, activeType, true);
      }

      /* hitbox */
      const hit = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "rect"
      );
      hit.setAttribute("x", x - 8);
      hit.setAttribute("y", staff.topY);
      hit.setAttribute("width", 16);
      hit.setAttribute("height", staff.bottomY - staff.topY);
      hit.setAttribute("fill", "transparent");
      hit.setAttribute("opacity", "0.1");
      hit.style.cursor = "pointer";
      hit.style.touchAction = "manipulation";
      hit.style.webkitTapHighlightColor = "transparent";

      /*
       * iPad Safari can synthesize a hover-like pointerenter before the tap.
       * Checking pointerType alone is not sufficient on every iPad/browser
       * mode, because that synthetic event can sometimes look like a mouse.
       * Only enable the dotted preview on hardware that truly supports hover.
       *
       * Commit on pointerdown rather than waiting for pointerup/click. This
       * prevents a hover state update or SVG redraw from replacing the hitbox
       * before the placement event is handled.
       */
      const supportsTrueHover = () =>
        window.matchMedia?.("(hover: hover) and (pointer: fine)")?.matches === true;

      hit.addEventListener("pointerenter", () => {
        if (!supportsTrueHover()) return;
        if (modeRef.current !== "review") {
          setActiveSlot(slotId);
        }
      });

      hit.addEventListener("pointerleave", () => {
        if (!supportsTrueHover()) return;
        if (modeRef.current !== "review") {
          setActiveSlot(null);
        }
      });

      hit.addEventListener("pointerdown", event => {
        if (modeRef.current === "review") return;
        if (event.pointerType === "mouse" && event.button !== 0) return;

        event.preventDefault();
        event.stopPropagation();

        // Do not leave a dotted preview over the newly placed barline.
        setActiveSlot(null);

        const current = valueRef.current || {};
        const next = { ...current };

        if (next[slotId]) {
          delete next[slotId];
        } else {
          next[slotId] = activeTypeRef.current;
        }

        valueRef.current = next;
        onChange(next);
      });

      layer.appendChild(hit);
    }
  }

  /* ───────── RENDER ───────── */

  const barOptions = config.testEndBarline ? ["single","double"] : ["single"];
  return (
    <QuestionLayout
      number={config.number}
      prompt={config.prompt}
      points={config.points}
      mode={mode}
      feedback={feedback}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "100%",
          overflow: "visible"
        }}
      >
        <div
          ref={containerRef}
          style={{
            // A fixed inner width prevents OSMD from reflowing the rhythm
            // into multiple systems on an iPad. QuestionLayout scrolls instead.
            width: config.scoreWidth ?? config.osmdWidth ?? 1400,
            minWidth: config.scoreWidth ?? config.osmdWidth ?? 1400,
            maxWidth: "none"
          }}
        />
      </div>

      {mode !== "review" && (
        <div style={{ display: "flex", gap: 12, marginTop: 16 }}>
          {barOptions.map(t => (
            <div
              key={t}
              onClick={() => setActiveType(t)}
              style={{
                padding: "6px 12px",
                border:
                  activeType === t
                    ? "2px solid #007aff"
                    : "1px solid #ccc",
                cursor: "pointer",
                fontWeight: "bold"
              }}
            >
              {t === "single" ? "|" : "||"}
            </div>
          ))}
        </div>
      )}
    </QuestionLayout>
  );
}
